import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance with interceptors
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available (checks both user and admin tokens)
api.interceptors.request.use(
  (config) => {
    // Check for admin token first (for admin panel requests)
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
      return config;
    }
    
    // Fall back to user token (for regular user requests)
    const userToken = localStorage.getItem('phantomtrades_token');
    if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't auto-redirect for auth/me endpoint - let AuthContext handle it
      const requestUrl = error.config?.url || '';
      if (requestUrl.includes('/auth/me')) {
        return Promise.reject(error);
      }
      
      // Check if this is an admin session or user session
      const isAdminSession = localStorage.getItem('adminToken');
      
      if (isAdminSession) {
        // Admin token expired or invalid
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminInfo');
        window.location.href = '/admin/login';
      } else {
        // User token expired or invalid - only redirect if not already on signin page
        const currentPath = window.location.pathname;
        if (currentPath !== '/signin' && currentPath !== '/signup') {
          localStorage.removeItem('phantomtrades_token');
          localStorage.removeItem('phantomtrades_user');
          window.location.href = '/signin';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Crypto API
export const cryptoAPI = {
  getList: async (limit = 50) => {
    try {
      const response = await api.get(`/crypto/list?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching crypto list:', error);
      throw error;
    }
  },

  getById: async (cryptoId) => {
    try {
      const response = await api.get(`/crypto/${cryptoId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching crypto details:', error);
      throw error;
    }
  },

  getPrice: async (cryptoId) => {
    try {
      const response = await api.get(`/crypto/${cryptoId}/price`);
      return response.data;
    } catch (error) {
      console.error('Error fetching crypto price:', error);
      throw error;
    }
  },
};

// Auth API
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Trading API
export const tradingAPI = {
  buy: async (data) => {
    const response = await api.post('/trade/buy', data);
    return response.data;
  },

  sell: async (data) => {
    const response = await api.post('/trade/sell', data);
    return response.data;
  },

  swap: async (data) => {
    const response = await api.post('/trade/swap', data);
    return response.data;
  },

  getSwapHistory: async (limit = 50) => {
    const response = await api.get(`/trade/swap/history/detailed?limit=${limit}`);
    return response.data;
  },

  // Order management
  placeOrder: async (data) => {
    const response = await api.post('/trade/orders', data);
    return response.data;
  },

  getActiveOrders: async () => {
    const response = await api.get('/trade/orders/active');
    return response.data;
  },

  getOrderHistory: async (limit = 50) => {
    const response = await api.get(`/trade/orders/history?limit=${limit}`);
    return response.data;
  },

  cancelOrder: async (orderId) => {
    const response = await api.delete(`/trade/orders/${orderId}`);
    return response.data;
  },
};

// Wallet API
export const walletAPI = {
  deposit: async (data) => {
    const response = await api.post('/wallet/deposit', data);
    return response.data;
  },

  withdraw: async (data) => {
    const response = await api.post('/wallet/withdraw', data);
    return response.data;
  },

  getTransactions: async (limit = 50) => {
    const response = await api.get(`/wallet/transactions?limit=${limit}`);
    return response.data;
  },

  getBalance: async () => {
    const response = await api.get('/wallet/balance');
    return response.data;
  },

  // Get crypto holdings with balances
  getCryptoHoldings: async () => {
    const response = await api.get('/wallet/crypto-holdings');
    return response.data;
  },

  // Active deposits and withdrawals
  getActiveDeposits: async () => {
    const response = await api.get('/deposits/active');
    return response.data;
  },

  getActiveWithdrawals: async () => {
    const response = await api.get('/withdrawals/active');
    return response.data;
  },

  // Deposit and withdrawal history
  getDepositHistory: async (limit = 50) => {
    const response = await api.get(`/deposits/history?limit=${limit}`);
    return response.data;
  },

  getWithdrawalHistory: async (limit = 50) => {
    const response = await api.get(`/withdrawals/history?limit=${limit}`);
    return response.data;
  },

  // Get ALL transaction history (deposits + withdrawals combined)
  getAllTransactionHistory: async (limit = 100) => {
    const response = await api.get(`/transactions/all-history?limit=${limit}`);
    return response.data;
  },

  // Get user's withdrawal requests (all statuses)
  getMyWithdrawals: async () => {
    const response = await api.get('/withdrawals/my-requests');
    return response.data;
  },
};

// Portfolio API
export const portfolioAPI = {
  get: async () => {
    const response = await api.get('/portfolio');
    return response.data;
  },
};

// AI Arbitrage API
export const arbitrageAPI = {
  getConfig: async () => {
    const response = await api.get('/arbitrage/config');
    return response.data;
  },

  invest: async (data) => {
    const response = await api.post('/arbitrage/invest', data);
    return response.data;
  },

  getActiveInvestments: async () => {
    const response = await api.get('/arbitrage/investments/active');
    return response.data;
  },

  getInvestmentHistory: async () => {
    const response = await api.get('/arbitrage/investments/history');
    return response.data;
  },

  getDetailedInvestmentHistory: async (limit = 50) => {
    const response = await api.get(`/arbitrage/investments/history/detailed?limit=${limit}`);
    return response.data;
  },

  settleInvestment: async (investmentId) => {
    const response = await api.post(`/arbitrage/investments/${investmentId}/settle`);
    return response.data;
  },

  cancelInvestment: async (investmentId) => {
    const response = await api.delete(`/arbitrage/investments/${investmentId}`);
    return response.data;
  },

  getInvestmentDetails: async (investmentId) => {
    const response = await api.get(`/arbitrage/investments/${investmentId}`);
    return response.data;
  },
};

// Binary Trading API
export const priceAlertsAPI = {
  list: async () => (await api.get('/price-alerts')).data,
  create: async (data) => (await api.post('/price-alerts', data)).data,
  remove: async (id) => (await api.delete(`/price-alerts/${id}`)).data,
  reactivate: async (id) => (await api.post(`/price-alerts/${id}/reactivate`)).data,
  check: async () => (await api.post('/price-alerts/check')).data,
};

export const binaryAPI = {
  getLivePrice: async (cryptoId, symbol) => (await api.get(`/binary/price/${cryptoId}`, { params: { symbol } })).data,
  getConfig: async () => {
    const response = await api.get('/binary/config');
    return response.data;
  },

  placeTrade: async (data) => {
    const response = await api.post('/binary/trade', data);
    return response.data;
  },

  getActiveTrades: async () => {
    const response = await api.get('/binary/trades/active');
    return response.data;
  },

  getTradeHistory: async (limit = 50) => {
    const response = await api.get(`/binary/trades/history?limit=${limit}`);
    return response.data;
  },

  getDetailedTradeHistory: async (limit = 50) => {
    const response = await api.get(`/binary/trades/history/detailed?limit=${limit}`);
    return response.data;
  },

  settleTrade: async (tradeId) => {
    const response = await api.post(`/binary/trades/${tradeId}/settle`);
    return response.data;
  },

  cancelTrade: async (tradeId) => {
    const response = await api.delete(`/binary/trades/${tradeId}`);
    return response.data;
  },
};

export default api;
